import React, { useEffect, useState } from 'react'
import api from '../api/axios'
import * as XLSX from 'xlsx'
import { Download, RefreshCw, Building2, CheckCircle, Users, CreditCard, ChevronDown, ChevronUp } from 'lucide-react'

function ClientRow({ c, expanded, setExpanded }: { c: any, expanded: string | null, setExpanded: (id: string | null) => void }) {
  return (
    <div>
      <div className="px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 cursor-pointer gap-2"
        onClick={() => setExpanded(expanded === c.clientId ? null : c.clientId)}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-orange-600 font-bold text-sm">{c.name[0]}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy-800 truncate">{c.name}</p>
            <p className="text-xs text-gray-400 truncate">{c.companyName || c.clientCode}</p>
            <p className="text-xs text-gray-400 truncate sm:hidden">{c.email}</p>
            <div className="flex items-center gap-3 sm:hidden mt-1">
              <span className="text-xs text-navy-700 font-medium">{c.totalJobs} jobs</span>
              <span className="text-xs text-green-600 font-medium">{c.completedJobs} done</span>
              {c.activeSubscription && (
                <span className="text-xs text-orange-500 font-medium">{c.activeSubscription.daysLeft}d left</span>
              )}
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-6 flex-shrink-0">
          <div className="text-center">
            <p className="text-sm font-bold text-navy-800">{c.totalJobs}</p>
            <p className="text-xs text-gray-400">Total Jobs</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-green-600">{c.completedJobs}</p>
            <p className="text-xs text-gray-400">Completed</p>
          </div>
          {c.activeSubscription ? (
            <div className="text-center">
              <p className="text-sm font-bold text-orange-600">{c.activeSubscription.daysLeft}d</p>
              <p className="text-xs text-gray-400">Days Left</p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">No Sub</p>
            </div>
          )}
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium hidden sm:inline ${c.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
            {c.status}
          </span>
          {expanded === c.clientId ? <ChevronUp size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />}
        </div>
      </div>

      {expanded === c.clientId && (
        <div className="px-6 pb-5 bg-gray-50/50">
          {c.activeSubscription && (
            <div className="mb-4 p-4 bg-orange-50 border border-orange-100 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-orange-700">Active Subscription — {c.activeSubscription.planName}</p>
                <span className="text-xs text-orange-500">{new Date(c.activeSubscription.startDate).toLocaleDateString('en-IN')} — {new Date(c.activeSubscription.endDate).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                <span>{c.activeSubscription.jobsUsed} / {c.activeSubscription.totalJobsAllowed} jobs used</span>
                <span>{c.activeSubscription.daysLeft} days remaining</span>
                {c.activeSubscription.price && <span>${c.activeSubscription.price}</span>}
              </div>
              <div className="h-1.5 bg-orange-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-400 rounded-full"
                  style={{ width: `${Math.min(100, Math.round((c.activeSubscription.jobsUsed / c.activeSubscription.totalJobsAllowed) * 100))}%` }} />
              </div>
            </div>
          )}
          {c.subscriptionHistory.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Subscription History</p>
              {/* Mobile: card style */}
              <div className="sm:hidden space-y-2">
                {c.subscriptionHistory.map((s: any) => (
                  <div key={s.subscriptionId} className="bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-navy-800">{s.planName}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {s.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(s.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {new Date(s.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    <div className="flex gap-3 text-xs text-gray-600">
                      <span>Jobs: <strong>{s.jobsUsed}/{s.totalJobsAllowed}</strong></span>
                      {s.price && <span>Price: <strong>${s.price}</strong></span>}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop: table style */}
              <table className="w-full text-xs hidden sm:table">
                <thead>
                  <tr className="text-gray-400">
                    <th className="text-left py-1.5">Plan</th>
                    <th className="text-left py-1.5">Period</th>
                    <th className="text-right py-1.5">Jobs</th>
                    <th className="text-right py-1.5">Used</th>
                    <th className="text-right py-1.5">Price</th>
                    <th className="text-left py-1.5 pl-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {c.subscriptionHistory.map((s: any) => (
                    <tr key={s.subscriptionId} className="text-navy-700">
                      <td className="py-2 font-medium">{s.planName}</td>
                      <td className="py-2 text-gray-500">
                        {new Date(s.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {new Date(s.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-2 text-right">{s.totalJobsAllowed}</td>
                      <td className="py-2 text-right">{s.jobsUsed}</td>
                      <td className="py-2 text-right">{s.price ? `$${s.price}` : '—'}</td>
                      <td className="py-2 pl-4">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${s.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-gray-400">No subscription history — email/pay-per-job client</p>
          )}
        </div>
      )}
    </div>
  )
}

export default function ClientReportsPage() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [clientId, setClientId] = useState('')
  const [subStatus, setSubStatus] = useState('')
  const [clients, setClients] = useState<any[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [showDeactivated, setShowDeactivated] = useState(false)

  useEffect(() => {
    api.get('/clients').then(res => setClients(res.data)).catch(() => {})
    fetchReport()
  }, [])

  const fetchReport = async () => {
    setLoading(true)
    try {
      let url = `/reports/clients?`
      if (dateFrom) url += `&dateFrom=${dateFrom}`
      if (dateTo) url += `&dateTo=${dateTo}`
      if (clientId) url += `&clientId=${clientId}`
      if (subStatus) url += `&status=${subStatus}`
      const res = await api.get(url)
      setData(res.data)
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const handleExportXLSX = () => {
    if (!data) return
    const wb = XLSX.utils.book_new()

    // Summary
    const summaryData = [
      ['Metric', 'Value'],
      ['Total Clients', data.summary.totalClients],
      ['Active Clients', data.summary.activeClients],
      ['Subscription Clients', data.summary.subscriptionClients],
      ['Total Subscriptions', data.summary.totalSubscriptions],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary')

    // Clients overview
    const clientsData = [
      ['Client Code', 'Name', 'Company', 'Email', 'Type', 'Status', 'Total Jobs', 'Completed', 'Active Sub', 'Jobs Allowed', 'Jobs Used', 'Days Left'],
      ...data.clients.map((c: any) => [
        c.clientCode, c.name, c.companyName || '—', c.email,
        c.entryType, c.status, c.totalJobs, c.completedJobs,
        c.activeSubscription ? 'Yes' : 'No',
        c.activeSubscription?.totalJobsAllowed || '—',
        c.activeSubscription?.jobsUsed || '—',
        c.activeSubscription?.daysLeft ?? '—'
      ])
    ]
    const clientsWs = XLSX.utils.aoa_to_sheet(clientsData)
    clientsWs['!cols'] = clientsData[0].map((h: string) => ({ wch: Math.max(String(h).length, 12) }))
    XLSX.utils.book_append_sheet(wb, clientsWs, 'Clients')

    // Subscription history
    const subRows: any[] = [['Client', 'Plan', 'Jobs Allowed', 'Jobs Used', 'Start', 'End', 'Status', 'Price']]
    data.clients.forEach((c: any) => {
      c.subscriptionHistory.forEach((s: any) => {
        subRows.push([
          c.name, s.planName, s.totalJobsAllowed, s.jobsUsed,
          new Date(s.startDate).toLocaleDateString('en-IN'),
          new Date(s.endDate).toLocaleDateString('en-IN'),
          s.status, s.price ? `$${s.price}` : '—'
        ])
      })
    })
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(subRows), 'Subscriptions')

    XLSX.writeFile(wb, `client-report-${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const filteredClients = data?.clients?.filter((c: any) =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.companyName?.toLowerCase().includes(search.toLowerCase()) ||
    c.clientCode?.toLowerCase().includes(search.toLowerCase())
  ) || []

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">Client Reports</h1>
          <p className="text-gray-400 mt-1 text-sm">Subscription history and client job overview</p>
        </div>
        <button onClick={handleExportXLSX} disabled={!data}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white text-sm px-4 py-2.5 rounded-xl font-medium transition-colors">
          <Download size={15} /> Export XLSX
        </button>
      </div>

      {/* Filters */}
      <div className="card p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">From</label>
            <input type="date" className="input text-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input text-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="label">Client</label>
            <select className="input text-sm" value={clientId} onChange={e => setClientId(e.target.value)}>
              <option value="">All Clients</option>
              {clients.map((c: any) => (
                <option key={c.clientId} value={c.clientId}>{c.companyName || c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Subscription Status</label>
            <select className="input text-sm" value={subStatus} onChange={e => setSubStatus(e.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(dateFrom || dateTo || clientId || subStatus) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setClientId(''); setSubStatus('') }}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-500 transition-colors">Clear Filters</button>
          )}
          <button onClick={fetchReport} disabled={loading}
            className="flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium transition-colors ml-auto">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      {!data ? (
        <div className="card p-20 text-center text-gray-400">Run the report to see results</div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Clients', value: data.summary.totalClients, icon: Users, color: 'text-navy-600', bg: 'bg-navy-50' },
              { label: 'Active Clients', value: data.summary.activeClients, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Subscription Clients', value: data.summary.subscriptionClients, icon: CreditCard, color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Total Subscriptions', value: data.summary.totalSubscriptions, icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50' },
            ].map(({ label, value }) => (
              <div key={label} className="card p-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none">{value}</p>
                  <p className="text-xs font-light text-gray-400 leading-none">{label}</p>
                </div>
              </div>
            ))}
          </div>

          

          {/* Search */}
          <div className="flex justify-end">
            <input type="text" placeholder="Search clients..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:border-orange-400" />
          </div>

          {/* Subscription Clients */}
          {(() => {
            const subClients = filteredClients.filter((c: any) => c.subscriptionHistory.length > 0)
const nonSubClients = filteredClients.filter((c: any) => c.subscriptionHistory.length === 0 && c.status === 'active')
const deactivatedClients = filteredClients.filter((c: any) => c.status === 'inactive')
const activeSubClients = subClients.filter((c: any) => 
  c.activeSubscription && c.activeSubscription.daysLeft > 0 && 
  c.activeSubscription.jobsUsed < c.activeSubscription.totalJobsAllowed
)
const inactiveSubClients = subClients.filter((c: any) => 
  !c.activeSubscription || c.activeSubscription.daysLeft <= 0 || 
  c.activeSubscription.jobsUsed >= c.activeSubscription.totalJobsAllowed
)

            return (
              <div className="space-y-6">
                {/* Subscription Clients */}
<div className="card overflow-hidden">
  <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
    <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
      <CreditCard size={14} className="text-green-500" />
    </div>
    <div>
      <p className="font-display font-semibold text-navy-900">Active Subscription Clients</p>
      <p className="text-xs text-gray-400">{activeSubClients.length} clients with valid subscription</p>
    </div>
  </div>
  <div className="divide-y divide-gray-50">
    {activeSubClients.length === 0 ? (
      <div className="py-10 text-center text-gray-400 text-sm">No active subscription clients</div>
    ) : activeSubClients.map((c: any) => (
      <ClientRow key={c.clientId} c={c} expanded={expanded} setExpanded={setExpanded} />
    ))}
  </div>
</div>
<div className="card overflow-hidden">
  <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
    <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
      <CreditCard size={14} className="text-red-400" />
    </div>
    <div>
      <p className="font-display font-semibold text-navy-900">Expired Subscription Clients</p>
      <p className="text-xs text-gray-400">{inactiveSubClients.length} clients with expired or exhausted subscription</p>
    </div>
  </div>
  <div className="divide-y divide-gray-50">
    {inactiveSubClients.length === 0 ? (
      <div className="py-10 text-center text-gray-400 text-sm">No expired subscription clients</div>
    ) : inactiveSubClients.map((c: any) => (
      <ClientRow key={c.clientId} c={c} expanded={expanded} setExpanded={setExpanded} />
    ))}
  </div>
</div>

                {/* Non-Subscription Clients */}
                <div className="card overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                    <div className="w-8 h-8 bg-navy-50 rounded-lg flex items-center justify-center">
                      <Building2 size={14} className="text-navy-500" />
                    </div>
                    <div>
                      <p className="font-display font-semibold text-navy-900">Non-Subscription Clients</p>
                      <p className="text-xs text-gray-400">{nonSubClients.length} email / pay-per-job clients</p>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {nonSubClients.length === 0 ? (
                      <div className="py-10 text-center text-gray-400 text-sm">No non-subscription clients found</div>
                    ) : nonSubClients.map((c: any) => (
                      <ClientRow key={c.clientId} c={c} expanded={expanded} setExpanded={setExpanded} />
                    ))}
                  </div>
                </div>
                {/* Deactivated Clients */}
                {deactivatedClients.length > 0 && (
                  <div>
                    <button onClick={() => setShowDeactivated((v: boolean) => !v)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors mb-3">
                      {showDeactivated ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                      Deactivated Clients ({deactivatedClients.length})
                    </button>
                    {showDeactivated && (
                      <div className="card overflow-hidden opacity-75">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                          <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                            <Building2 size={14} className="text-red-400" />
                          </div>
                          <div>
                            <p className="font-display font-semibold text-navy-900">Deactivated Clients</p>
                            <p className="text-xs text-gray-400">{deactivatedClients.length} inactive client accounts</p>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {deactivatedClients.map((c: any) => (
                            <ClientRow key={c.clientId} c={c} expanded={expanded} setExpanded={setExpanded} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })()}
        </>
      )}
    </div>
  )
}